import { Injectable, inject } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { SwalAlertService } from 'src/app/services/swal-alert.service';
import { ToastService } from 'src/app/services/toast.service';
import { PoliticaEntregaExcelImportResponseDto } from '../../../models/politica-entrega.dto';

@Injectable()
export class PoliticasEntregaUiEffectsService {
  private readonly toast = inject(ToastService);
  private readonly swal = inject(SwalAlertService);
  private readonly spinner = inject(NgxSpinnerService);

  syncLoading(isLoading: boolean): void {
    if (isLoading) {
      this.spinner.show();
      return;
    }
    this.spinner.hide();
  }

  showRegionesLoadError(): void {
    this.toast.showAdminError('No se pudieron cargar las regiones.');
  }

  showPoliticasLoadError(): void {
    this.toast.showAdminError('No se pudieron cargar las políticas de entrega.');
  }

  showGuardarSuccess(): void {
    this.toast.showAdminSuccess('Política guardada correctamente.');
  }

  showGuardarError(): void {
    this.toast.showAdminError('Error al guardar la política.');
  }

  showEliminarSuccess(): void {
    this.toast.showAdminSuccess('Política eliminada correctamente.');
  }

  showEliminarError(): void {
    this.toast.showAdminError('Error al eliminar la política.');
  }

  /**
   * Prioriza `user_message` del backend. Si hubo filas omitidas (`failed > 0`),
   * muestra el mensaje como advertencia; si existe `conflict_examples`, lista combinaciones ejemplo.
   */
  showExcelImportResult(result: PoliticaEntregaExcelImportResponseDto): void {
    const text = result.user_message?.trim();
    if (result.failed > 0) {
      const fallback =
        text ??
        `Carga parcial: se importaron ${result.imported} filas y se omitieron ${result.failed}. Revise datos geográficos o centros logísticos e intente de nuevo.`;
      const html = this.buildPoliticaImportWarningHtml(fallback, result);
      void Swal.fire({
        icon: 'warning',
        title: 'Atención',
        html,
        confirmButtonText: 'OK',
        width: 'min(640px, 96vw)',
      });
      return;
    }
    this.toast.showAdminSuccess(text ?? 'Importación completada correctamente.');
  }

  private buildPoliticaImportWarningHtml(
    userMessage: string,
    result: PoliticaEntregaExcelImportResponseDto,
  ): string {
    let html = `<p class="text-start mb-3">${escapeHtml(userMessage)}</p>`;
    const ex = result.conflict_examples;
    if (!ex || typeof ex !== 'object') {
      return html;
    }
    const entries = Object.entries(ex).filter(([, lines]) => Array.isArray(lines) && lines.length > 0);
    if (entries.length === 0) {
      return html;
    }
    html +=
      '<p class="text-start small text-muted mb-2">Detalle de combinaciones distintas omitidas (muestra acotada):</p>';
    for (const [code, lines] of entries) {
      const title = escapeHtml(this.politicaConflictSectionTitle(code));
      const items = lines
        .map((line) => `<li class="mb-1">${escapeHtml(String(line))}</li>`)
        .join('');
      html += `<p class="text-start fw-semibold mb-1 small">${title}</p><ul class="text-start small mb-3 ps-3" style="max-height:260px;overflow:auto">${items}</ul>`;
    }
    return html;
  }

  private politicaConflictSectionTitle(code: string): string {
    const map: Record<string, string> = {
      comuna_no_existe: 'Comuna no registrada en datos geográficos',
      comuna_region_invalida: 'Comuna no corresponde a la región indicada',
      centro_no_existe: 'Centro no registrado en centros logísticos',
      region_no_existe: 'Región no existe en catálogo',
    };
    return map[code] ?? code;
  }

  showExcelError(): void {
    this.toast.showAdminError('Error al subir el archivo.');
  }
}

function escapeHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}
